import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import securedAccountIcon from '@/assets/images/secured-account.png';
import BasicButton from '@/components/Common/Button/BasicButton';
import AlertModal from '@/components/Common/Modal/AlertModal';
import MenuHeader from '@/components/Header/MenuHeader';
import { InformationCircleIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { useMemberContext } from '@/contexts/Member/MemberContext';
import useNiceMobileAuth from '@/hooks/useNiceMobileAuth';

const OBLIGATIONS = [
  '미인증 시 차량 매물 등록이 법적으로 불가합니다',
  '인증 정보는 실소유자 확인 목적으로만 활용됩니다',
  '허위 매물 등록 시 관련 법령에 따라 처벌받을 수 있습니다',
  '본인인증은 최초 1회만 진행합니다.',
];

const IdentityVerification = () => {
  const navigate = useNavigate();
  const { member, getMember } = useMemberContext();
  const { showToast } = useToastContext();

  const [isMismatchOpen, setIsMismatchOpen] = useState(false);

  const isVerified = Number(member?.authentication) === 1;

  const { isLoading, startVerification } = useNiceMobileAuth({
    memberId: member?.id,
    onSuccess: () => {
      if (member?.id) {
        getMember?.(member.id);
      }
      showToast('본인인증이 완료되었습니다.', 'success', 'bottom');
      navigate('/more');
    },
    onError: (message) => showToast(message, 'error', 'bottom'),
    onPhoneMismatch: () => setIsMismatchOpen(true),
  });

  const handleStart = () => {
    if (isVerified || isLoading) return;
    startVerification();
  };

  return (
    <>
      <MenuHeader title="본인인증" path="/more"></MenuHeader>
      <div className="w-full px-4 pt-[60px] pb-[140px]">
        <div className="flex flex-col items-center pt-8 text-center">
          <div
            className="flex items-center justify-center rounded-2xl"
            style={{ width: 64, height: 64, backgroundColor: colors.blue[5] }}
          >
            <img src={securedAccountIcon} alt="본인인증" className="w-10 h-10" />
          </div>
          <h1 className="mt-6 text-[24px] font-bold leading-[32px] text-gray-9">
            매물 등록 전
            <br />
            본인인증이 필요합니다
          </h1>
          <p className="mt-3 text-[14px] leading-[22px] text-gray-6">
            허위 매물 방지 및 안전한 거래를 위해
            <br />
            NICE 본인인증을 1회 완료해야 합니다.
            <br />
            인증 후에는 별도 재인증 없이 등록 가능합니다.
          </p>
        </div>

        <div className="p-4 mt-8 rounded-xl" style={{ backgroundColor: colors.secondaryBlue.faq }}>
          <p className="text-[14px] font-semibold" style={{ color: colors.primary[10] }}>
            국토교통부 시행 의무사항
          </p>
          <ul className="flex flex-col gap-3 mt-3">
            {OBLIGATIONS.map((obligation) => (
              <li key={obligation} className="flex">
                <span
                  className="mt-[8px] mr-2 rounded-full shrink-0"
                  style={{ width: 4, height: 4, backgroundColor: colors.primary[10] }}
                />
                <span className="text-[14px] leading-[20px]" style={{ color: colors.primary[10] }}>
                  {obligation}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex gap-2 p-3 mt-4 rounded-xl" style={{ backgroundColor: colors.secondaryYellow[0] }}>
          <span className="mt-[1px] shrink-0">
            <InformationCircleIcon color="#CA8A04" width={18} height={18} />
          </span>
          <p className="text-[13px] leading-[20px]" style={{ color: '#854D0E' }}>
            「자동차관리법」과 「자동차관리법 시행령」에 근거하여 국토교통부가 시행하는 정책입니다. NICE평가정보(주)에
            이름·생년월일·성별·휴대폰번호가 제공됩니다. 수집된 CI는 암호화 저장 후 중복 계정 방지 목적으로만 사용됩니다.
          </p>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 mx-auto max-w-[720px] min-w-[280px] px-4 pt-3 pb-4 border-t bg-gray-0 border-gray-2">
        <BasicButton
          name={isVerified ? '본인인증 완료' : isLoading ? '본인인증 진행 중...' : 'NICE 본인인증 시작'}
          bgColor={isVerified ? colors.gray[2] : colors.primary[10]}
          borderColor={isVerified ? colors.gray[3] : colors.primary[10]}
          textColor={isVerified ? colors.gray[6] : colors.gray[0]}
          fontSize={16}
          height={52}
          fontWeight="bold"
          onClick={handleStart}
          isDisabled={isVerified || isLoading}
        />
        <p className="mt-3 text-center text-[14px] text-gray-6 cursor-pointer" onClick={() => navigate('/more')}>
          나중에 하기
        </p>
      </div>

      <AlertModal
        isOpen={isMismatchOpen}
        onClose={() => setIsMismatchOpen(false)}
        title="가입한 휴대전화번호와 인증한 전화번호가 다릅니다"
        content={
          <p className="text-[14px] leading-[22px] text-gray-7 text-center">
            신규 회원가입 또는 가입된 휴대전화 번호로
            <br />
            실명인증 해주세요.
          </p>
        }
        buttonLabel="확인"
      />
    </>
  );
};

export default IdentityVerification;
